import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { BaseIndexPageComponent } from 'src/app/components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from 'src/app/components/pages/base-index-page/base-index-page.config';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { DataTableColumnText } from 'src/app/components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';
import { DataTableColumnActions } from 'src/app/components/widgets/data-table/columns/data-table-column-actions/data-table-column-actions.config';
import ApiOptionsConfig from 'src/app/services/api/api-options.config';
import { RegulatorsService } from 'src/app/services/api/regulators.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { FormService } from 'src/app/services/form.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { RouteService } from 'src/app/services/route.service';
import { Regulator, RegulatorClient, RegulatorClientPayload } from 'src/app/models/entities/regulator';
import { RegulatorContextBoxComponent, RegulatorContextItem } from '../components/regulator-context-box/regulator-context-box.component';
import { apiErrorMessage, isValidationError, withDeleteFeedback } from '../regulators-ui.utils';

/** Client registry of a single regulator: every client belongs to one regulator. */
@Component({
  selector: 'app-regulator-clients-page',
  imports: [BaseIndexPageComponent, ReactiveFormsModule, InputFieldComponent, RegulatorContextBoxComponent],
  templateUrl: './regulator-clients-page.component.html',
  styleUrl: './regulator-clients-page.component.scss',
})
export class RegulatorClientsPageComponent implements OnInit, OnDestroy {

  @ViewChild(BaseIndexPageComponent) private indexPage?: BaseIndexPageComponent;
  @ViewChild('clientModal') private clientModal!: TemplateRef<unknown>;

  baseIndexPageConfig: BaseIndexPageConfig;
  contextItems: RegulatorContextItem[] = [];

  readonly regulatorId: number;

  form = new FormGroup({
    code: new FormControl<string>('', { nonNullable: true, validators: Validators.required }),
    name: new FormControl<string>('', { nonNullable: true, validators: Validators.required }),
  });

  editingClient: RegulatorClient | null = null;
  isSaving = false;

  readonly deleteService = {
    delete: (clientId: number) =>
      withDeleteFeedback(this.regulatorsService.deleteClient(this.regulatorId, clientId), this.alertNotificationService, 'Client')
  };

  private modalRef?: NgbModalRef;
  private subscriptions = new Subscription();

  constructor(
    protected regulatorsService: RegulatorsService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private alertNotificationService: AlertNotificationService,
    private formService: FormService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.regulatorId = Number(this.route.snapshot.paramMap.get('regulatorId'));

    this.baseIndexPageConfig = {
      pageTitle: 'Regulators',
      cardTitle: 'Clients',
      dataTable: {
        data: [],
        pagination: null,
        searchModel: {
          code: null,
          name: null,
        },
        columns: [
          new DataTableColumnText({
            attribute: 'code',
            header: 'Code',
            filter: 'text',
            width: '220px',
            value: (item: RegulatorClient) => item?.code ?? ''
          }),
          new DataTableColumnText({
            attribute: 'name',
            header: 'Name',
            filter: 'text',
            value: (item: RegulatorClient) => item?.name ?? ''
          }),
          new DataTableColumnActions({
            header: 'Actions',
            attribute: 'actions',
            sortable: false,
            cellClass: 'text-center',
            style: { width: '150px' },
            buttons: [
              {
                icon: 'fa-globe',
                tooltip: 'Client websites',
                aclAction: 'view',
                onClick: (item: RegulatorClient) => this.routeService.regulatorClientWebsites(this.regulatorId, item.clientId)
              },
              {
                icon: 'fa-edit',
                tooltip: 'Edit client',
                aclAction: 'modify',
                onClick: (item: RegulatorClient) => this.openForm(item)
              },
              {
                icon: 'fa-trash',
                tooltip: 'Delete client',
                aclAction: 'delete',
                isDelete: true
              },
            ]
          })
        ]
      },
      apiListService: (options: ApiOptionsConfig) => this.regulatorsService.listClients(this.regulatorId, options),
      responseListModelName: 'clients',
    };
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.mainLayoutService.activeProfile$.subscribe(profileCode => {
        const canCreate = this.accessControlService.hasFeatureActionAccess(profileCode, 'REGULATORS_LIST', 'modify');
        this.baseIndexPageConfig.addNewItemButton = canCreate
          ? { type: 'success', title: 'Create Client', onClick: () => this.openForm(null) }
          : undefined;
      })
    );

    this.regulatorsService.detail(this.regulatorId).subscribe({
      next: (response: any) => this.applyRegulator(response?.data?.regulator),
      error: () => this.router.navigate(['/error/404'])
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.modalRef?.dismiss();
    this.mainLayoutService.setBreadcrumbs(null);
  }

  goBack(): void {
    this.routeService.regulatorsIndex();
  }

  control(name: 'code' | 'name'): FormControl {
    return this.form.get(name) as FormControl;
  }

  openForm(client: RegulatorClient | null): void {
    this.editingClient = client;
    this.form.reset({ code: client?.code ?? '', name: client?.name ?? '' });
    this.modalRef = this.modalService.open(this.clientModal, { centered: true, backdrop: 'static' });
    this.modalRef.result.finally(() => this.modalRef = undefined).catch(() => undefined);
  }

  closeForm(): void {
    this.modalRef?.dismiss();
  }

  submitForm(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.isSaving) {
      return;
    }

    const value = this.form.getRawValue();
    const payload: RegulatorClientPayload = {
      code: value.code.trim(),
      name: value.name.trim(),
    };
    const request$ = this.editingClient
      ? this.regulatorsService.updateClient(this.regulatorId, this.editingClient.clientId, payload)
      : this.regulatorsService.createClient(this.regulatorId, payload);

    this.isSaving = true;
    request$.subscribe({
      next: () => {
        this.isSaving = false;
        this.alertNotificationService.success('Client saved successfully');
        this.modalRef?.close();
        this.indexPage?.reload();
      },
      error: (error: any) => {
        this.isSaving = false;
        if (isValidationError(error)) {
          this.formService.setValidationErrors(this.form, error.error.error.validationErrors);
        }
        this.alertNotificationService.error(apiErrorMessage(error, 'Error saving client'));
      }
    });
  }

  private applyRegulator(regulator: Regulator | null | undefined): void {
    if (!regulator) {
      return;
    }

    this.contextItems = [{ label: 'Regulator', code: regulator.code, value: regulator.name }];
    this.mainLayoutService.setBreadcrumbs([
      { label: 'Regulators', routerLink: '/regulators' },
      { label: regulator.code },
      { label: 'Clients' },
    ]);
  }
}
